/**
 * applyDetailSurface — patch a NodeMaterial with a procedural micro-normal +
 * roughness breakup (POM-pattern: caches prior nodes, returns an update/dispose
 * handle). No texture assets, no UV/tangent dependency (derivative-based TBN).
 */
import type * as THREE from 'three/webgpu';
import { float } from 'three/tsl';
import { Logger } from '../../../core/Logger.js';
import { createDetailConfig, type DetailSurfaceOptions } from './DetailConfig.js';
import { createDetailNodes } from './DetailNode.js';

export interface DetailHandle {
  /** Push config changes to the GPU. */
  update(): void;
  /** Restore the material's pre-detail nodes. */
  dispose(): void;
}

export function applyDetailSurface(
  material: THREE.MeshStandardNodeMaterial | THREE.NodeMaterial,
  opts: DetailSurfaceOptions = {},
): DetailHandle {
  const config = createDetailConfig(opts);
  const { detailNormal, roughnessFactor, keep, uniforms } = createDetailNodes(config);

  const matAny = material as unknown as { normalNode?: unknown; roughnessNode?: unknown; needsUpdate?: boolean };
  const prior = { normalNode: matAny.normalNode, roughnessNode: matAny.roughnessNode };

  // Detail normal (tangent-space; three composes the TBN via derivatives).
  matAny.normalNode = detailNormal;

  // Roughness breakup around the material's authored scalar roughness.
  const baseRough = (material as unknown as { roughness?: number }).roughness ?? 1.0;
  // Roughness breakup around the authored scalar, plus — when filtered — the
  // slope energy of the detail the footprint no longer resolves (≈ s²/2 for a
  // normal perturbation of amplitude s), so a distant surface roughens
  // instead of turning falsely glossy as its bumps average out.
  const unresolved = float(1).sub(keep);
  matAny.roughnessNode = float(baseRough)
    .add(roughnessFactor.mul(0.5).mul(config.roughnessBreakup))
    .add(unresolved.mul(config.roughnessTransfer * config.normalStrength * config.normalStrength))
    .clamp(0.04, 1.0);

  matAny.needsUpdate = true;
  Logger.debug('DetailSurface', `applied (tiling=${config.tiling}, normalStrength=${config.normalStrength}, roughnessBreakup=${config.roughnessBreakup})`);

  return {
    update(): void {
      (uniforms.tiling as unknown as { value: number }).value = config.tiling;
      (uniforms.normalStrength as unknown as { value: number }).value = config.normalStrength;
    },
    dispose(): void {
      matAny.normalNode = prior.normalNode;
      matAny.roughnessNode = prior.roughnessNode;
      matAny.needsUpdate = true;
    },
  };
}
